// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — doing things together in a meeting (`docs/MEETINGS_COLLAB_PLAN.md`):
 * the presenter's "Start an activity" sheet, and the bar at the top centre
 * that everyone sees while one runs.
 *
 *   - **Explore**: the audience's globe is theirs for a while (roomSync
 *     unlocks it); they can pin what they find, with a note. The presenter
 *     sees a heat map of where everyone looks and the pins, with "Show
 *     everyone" on each, and "Bring everyone back".
 *   - **Point**: everyone answers by tapping a place; on "Reveal" the
 *     presenter taps the right place (or none), and everyone sees all the
 *     answers and how far they were.
 *   - **Choice**: options to pick from; the presenter sees the tally live,
 *     and everyone sees it on "Reveal".
 *   - **Poll** (AYNI, owner 2026-10-12): options with no right answer, one
 *     or (if the presenter allows) several each; everyone sees the counts
 *     as they come, nobody sees who voted for what; "Close poll" ends the
 *     voting.
 *
 * **People** (`meetingPeopleUI.ts`, owner 2026-10-12): a name for each
 * participant, kept with their answers until the meeting ends; My answers;
 * and, for the presenter and the moderators, who is in the meeting, with
 * Give the globe, Take back, Mic off and Remove, and each activity's
 * answers by person. The presenter can take a handed-out globe back for a
 * moment (Take over) and hand it back.
 *
 * **Hand over the globe**: a participant raises a hand; the presenter
 * sees the raised hands and gives one of them the globe, which the room
 * then follows until the presenter takes it back or they give it back.
 * Activities stay the presenter's meanwhile.
 *
 * **Save as Khipu** (the Khipu plan, slice 2): the
 * presenter's page notes what the room was shown (`meetingRecorder.ts`)
 * and offers it as a session Khipu to download or keep on the account, or
 * plays it to the room as a recap in the Khipu player.
 *
 * A display name is optional, kept in this tab only, and shown to the
 * presenter and moderator with pins and raised hands. The room enforces every limit
 * (`roomActivity.ts`); this module only says them.
 *
 * Mounted in the WebXR overlay root, like the meeting line. Taps come from
 * the browser globe (`activityOverlay.ts`). Lives in `src/ui/` so
 * `check:i18n-strings` covers it.
 */

import { getLocale, t } from '../i18n'
import { formatDate } from '../i18n/format'
import { newKhipuId, type Khipu } from '../services/khipu'
import { saveKhipu } from '../services/khipuStore'
import { zipKhipu } from '../services/lessonService'
import type { RoomStatus } from '../services/roomClient'
import type { ActivityData } from '../services/roomClient'
import type { RoomClientMessage, RoomState } from '../services/roomProtocol'
import { buildMeetingKhipu, createMeetingRecorder } from '../services/meetingRecorder'
import {
  ACTIVITY_MAX_OPTIONS,
  ACTIVITY_MIN_OPTIONS,
  ACTIVITY_OPTION_CHARS,
  ACTIVITY_PROMPT_CHARS,
  EXPLORE_DURATIONS_MIN,
  LOOK_EVERY_MS,
  NAME_CHARS,
  PIN_NOTE_CHARS,
  PINS_PER_PERSON,
  QUESTION_DURATIONS_S,
  distanceKm,
  hasOptions,
  type ActivityKind,
  type MyAnswer,
  type RoomHand,
  type RoomPin,
} from '../services/roomActivity'
import { mapActivityGlobe, type ActivityGlobe } from '../services/activityOverlay'
import { clampPanel } from './floatingPanel'
import { mountMeetingPeople } from './meetingPeopleUI'
import type { Map as MaplibreMap } from 'maplibre-gl'

/** The activity overlay on the browser globe, with this module's words on it. */
export function activityGlobeFor(getMap: () => MaplibreMap | null): ActivityGlobe {
  return mapActivityGlobe(getMap, {
    showEveryone: t('activity.pin.show'),
    anonymous: t('activity.pin.anonymous'),
    answer: t('activity.answerPin'),
  })
}

/** The room, as far as activities need it (`RoomSyncHandle`). */
export interface ActivityRoom {
  onStatus(listener: (status: RoomStatus) => void): () => void
  onActivityData(listener: (data: ActivityData) => void): () => void
  send(message: Exclude<RoomClientMessage, { t: 'state' }>): void
  /** This device's own state, for the presenter's recording. */
  snapshot?(): RoomState
}

/** How often the presenter's page notes what it shows. */
const RECORD_EVERY_MS = 1000

/** Whether an activity's time limit has passed, on this device's clock. */
export function timeUp(a: { endsAt: number | null }, now: number = Date.now()): boolean {
  return a.endsAt !== null && now >= a.endsAt
}

/** Whether a question still takes answers: not revealed, and inside its time. */
export function answering(a: { closed: boolean; endsAt: number | null }, now: number = Date.now()): boolean {
  return !a.closed && !timeUp(a, now)
}

/** mm:ss, for a countdown. */
export function formatRemaining(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** mm:ss, for time so far. */
export function formatElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/**
 * What the activity's clock shows at `now`: time left, amber in the last
 * 30 seconds and red in the last 10, "Time's up" at the end; or time so
 * far when there is no limit. Null when there is nothing to show.
 */
export function timerView(
  a: { endsAt: number | null; startedAt: number | null },
  now: number,
): { text: string; label: string; state: 'running' | 'warn' | 'urgent' | 'done' | 'elapsed' } | null {
  if (a.endsAt !== null) {
    const left = a.endsAt - now
    if (left <= 0) return { text: t('activity.time.up'), label: t('activity.time.up'), state: 'done' }
    const text = formatRemaining(left)
    return { text, label: t('activity.time.left', { time: text }), state: left <= 10_000 ? 'urgent' : left <= 30_000 ? 'warn' : 'running' }
  }
  if (a.startedAt === null) return null
  const text = formatElapsed(now - a.startedAt)
  return { text, label: t('activity.time.elapsed', { time: text }), state: 'elapsed' }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text = ''): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text) node.textContent = text
  return node
}

function button(className: string, text: string): HTMLButtonElement {
  const b = el('button', className, text)
  b.type = 'button'
  // Taps on the overlay must not reach the AR session as a select.
  b.addEventListener('beforexrselect', (ev) => ev.preventDefault())
  return b
}

const show = (node: HTMLElement, on: boolean): void => {
  node.classList.toggle('hidden', !on)
}

/** How far one arrow-key press on the grip moves a window (Shift: ten times as far). */
const KEY_STEP = 16

/**
 * Let the presenter move an activity window (owner, 2026-10-12): dragged by
 * `head` (not by its buttons, but for the grip it adds), or moved with the
 * arrow keys from the grip; kept whole on screen. It floats over the globe
 * rather than pushing it: it is a dialog, open for a moment. A moved window
 * keeps its place while the meeting's page is open. Returns the teardown.
 */
export function makeDraggable(panel: HTMLElement, head: HTMLElement): () => void {
  const grip = button('activity-grip', '⠿')
  grip.setAttribute('aria-label', t('activity.sheet.move'))
  grip.title = t('activity.sheet.move')
  head.prepend(grip)
  head.classList.add('activity-drag-head')
  let moved: { x: number; y: number } | null = null

  function placeAt(x: number, y: number): void {
    const r = panel.getBoundingClientRect()
    if (!moved && r.width > 0) panel.style.width = `${Math.round(r.width)}px`
    moved = clampPanel(x, y, r.width, r.height, window.innerWidth, window.innerHeight)
    panel.classList.add('moved')
    panel.style.left = `${moved.x}px`
    panel.style.top = `${moved.y}px`
  }

  const onPointerDown = (e: PointerEvent): void => {
    const target = e.target as HTMLElement
    if (e.button > 0 || !head.contains(target)) return
    const pressed = target.closest('button')
    if (pressed && pressed !== grip) return
    e.preventDefault()
    const r = panel.getBoundingClientRect()
    const grabX = e.clientX - r.left
    const grabY = e.clientY - r.top
    panel.classList.add('carried')
    const move = (ev: PointerEvent): void => {
      if (ev.pointerId === e.pointerId) placeAt(ev.clientX - grabX, ev.clientY - grabY)
    }
    const end = (ev: PointerEvent): void => {
      if (ev.pointerId !== e.pointerId) return
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', end)
      document.removeEventListener('pointercancel', end)
      panel.classList.remove('carried')
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', end)
    document.addEventListener('pointercancel', end)
  }
  const onKeyDown = (e: KeyboardEvent): void => {
    const d = ({ ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] } as Record<string, [number, number]>)[e.key]
    if (!d) return
    e.preventDefault()
    const step = e.shiftKey ? KEY_STEP * 10 : KEY_STEP
    const r = panel.getBoundingClientRect()
    const from = moved ?? { x: r.left, y: r.top }
    placeAt(from.x + d[0] * step, from.y + d[1] * step)
  }
  // A smaller window, or a window grown taller (a choice's options), keeps it whole on screen.
  const keepOnScreen = (): void => {
    if (moved && !panel.classList.contains('hidden')) placeAt(moved.x, moved.y)
  }
  const resized = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(keepOnScreen)
  resized?.observe(panel)
  panel.addEventListener('pointerdown', onPointerDown)
  grip.addEventListener('keydown', onKeyDown)
  window.addEventListener('resize', keepOnScreen)
  return () => {
    resized?.disconnect()
    panel.removeEventListener('pointerdown', onPointerDown)
    window.removeEventListener('resize', keepOnScreen)
  }
}

/** Mount the activities for this page's seat. Returns the teardown. */
export function mountMeetingActivities(
  root: HTMLElement,
  room: ActivityRoom,
  globe: ActivityGlobe,
  opts: {
    code: string
    /** Play the meeting to the room as a recap (the Khipu player). */
    playKhipu?: (k: Khipu) => void
    /** Whether the plan keeps Khipus on the account. */
    canKeep?: () => boolean
    /** Whether downloads are the minimal exchange copy (a plan that keeps no Khipus). */
    exchangeOnly?: () => boolean
    /**
     * AYNI (multi-globe plan, phase 3): the globes on the presenter's page
     * (how many, and which is the main one), so a raised hand can be given
     * one of the others. Absent or one globe: only the whole globe is given.
     */
    globes?: () => { count: number; primary: number }
    /** A dataset's title in this app. */
    titleOf?: (datasetId: string) => string | null
    /**
     * AYNI (meeting games plan, decision D2): while a game is on the room
     * sends no typed names; a raised hand is then shown by the player's
     * game name, from the connection's id (`meetingGamesUI`).
     */
    gameNameOf?: (connId: string) => string | null
    /** AYNI (owner, 2026-10-12): this person leaves the meeting (Leave); absent, Leave is not offered. */
    onLeave?: () => void
  },
): () => void {
  const nameKey = `ayni-room-name:${opts.code}`
  const storedName = (): string | null => {
    try {
      return sessionStorage.getItem(nameKey)
    } catch {
      return null
    }
  }

  // ------------------------------------------------------- the top row
  const row = el('div', 'activity-row')
  const startPill = button('activity-start hidden', t('activity.start.button'))
  startPill.setAttribute('aria-haspopup', 'dialog')
  const handsPill = button('activity-start activity-hands-pill hidden', '')
  handsPill.setAttribute('aria-haspopup', 'true')
  const raisePill = button('activity-start activity-raise hidden', '')
  const khipuPill = button('activity-start activity-khipu-pill hidden', t('activity.khipu.pill'))
  khipuPill.setAttribute('aria-haspopup', 'true')
  row.append(startPill, handsPill, khipuPill, raisePill)
  const khipuMenu = el('div', 'activity-sheet activity-khipu hidden')
  khipuMenu.setAttribute('role', 'dialog')
  khipuMenu.setAttribute('aria-label', t('activity.khipu.title'))
  const khipuInfo = el('p', 'activity-khipu-info')
  const khipuDownload = button('activity-primary', t('activity.khipu.download'))
  const khipuSave = button('', t('activity.khipu.save'))
  const khipuPlay = button('', t('activity.khipu.play'))
  const khipuActions = el('div', 'activity-actions')
  khipuActions.append(khipuDownload, khipuSave, khipuPlay)
  const khipuStatus = el('p', 'activity-khipu-status')
  khipuStatus.setAttribute('role', 'status')
  khipuMenu.append(el('h2', '', t('activity.khipu.title')), khipuInfo, khipuActions, khipuStatus)
  const handsList = el('div', 'activity-sheet activity-hands hidden')
  handsList.setAttribute('role', 'dialog')
  handsList.setAttribute('aria-label', t('activity.hands.title'))
  const spotlightBar = el('section', 'activity-bar activity-spotlight hidden')
  spotlightBar.setAttribute('aria-live', 'polite')
  const spotlightText = el('p', 'activity-prompt')
  const spotlightActions = el('div', 'activity-actions')
  // AYNI (owner, 2026-10-12): the presenter takes a handed-out globe back for a moment, and hands it back.
  const takeOver = button('', t('activity.spotlight.takeOver'))
  const handBack = button('activity-primary', t('activity.spotlight.handBack'))
  const takeBack = button('activity-primary', t('activity.spotlight.takeBack'))
  const giveBack = button('activity-primary', t('activity.spotlight.giveBack'))
  spotlightActions.append(takeOver, handBack, takeBack, giveBack)
  spotlightBar.append(spotlightText, spotlightActions)
  // AYNI (multi-globe plan, phase 3): the globes handed out — the presenter's
  // list with Take back on each; a driver's own line with Give it back.
  const driversBar = el('section', 'activity-bar activity-drivers hidden')
  driversBar.setAttribute('aria-live', 'polite')
  const driversList = el('div', 'activity-drivers-list')
  driversBar.append(driversList)

  // ------------------------------------------------------------- the sheet
  const sheet = el('div', 'activity-sheet hidden')
  sheet.setAttribute('role', 'dialog')
  sheet.setAttribute('aria-label', t('activity.sheet.title'))
  const sheetTitle = el('h2', '', t('activity.sheet.title'))
  const sheetClose = button('activity-sheet-close', '✕')
  sheetClose.setAttribute('aria-label', t('activity.sheet.close'))
  const kinds = el('div', 'activity-kinds')
  kinds.setAttribute('role', 'radiogroup')
  let kind: ActivityKind = 'explore'
  const kindButtons = new Map<ActivityKind, HTMLButtonElement>()
  for (const k of ['explore', 'point', 'choice', 'poll'] as const) {
    const b = button('activity-kind', '')
    b.setAttribute('role', 'radio')
    b.append(el('strong', '', t(`activity.kind.${k}`)), el('span', '', t(`activity.kind.${k}.hint`)))
    b.addEventListener('click', () => {
      // Another kind asks another question: the box starts empty (owner, 2026-10-12).
      if (k !== kind) prompt.value = ''
      kind = k
      sheetError.textContent = ''
      renderSheet()
    })
    kindButtons.set(k, b)
    kinds.append(b)
  }
  const promptLabel = el('label', 'activity-field')
  const prompt = el('textarea')
  prompt.maxLength = ACTIVITY_PROMPT_CHARS
  prompt.rows = 2
  promptLabel.append(el('span', '', t('activity.prompt.label')), prompt)

  const durationBox = el('div', 'activity-field')
  durationBox.append(el('span', '', t('activity.duration.label')))
  const durations = el('div', 'activity-chips')
  // A limit per kind, in seconds: an explore runs minutes (3 by default),
  // a question is answered in seconds or a few minutes (no limit by default).
  const limitS: Record<ActivityKind, number | null> = { explore: 180, point: null, choice: null, poll: null }
  const durationLabel = (s: number | null): string =>
    s === null ? t('activity.duration.none') : s < 60 ? t('activity.duration.seconds', { n: s }) : t('activity.duration.minutes', { n: s / 60 })
  function renderDurations(): void {
    const offered = kind === 'explore' ? EXPLORE_DURATIONS_MIN.map((m) => m * 60) : [...QUESTION_DURATIONS_S]
    durations.replaceChildren(
      ...[...offered, null].map((s) => {
        const b = button('activity-chip', durationLabel(s))
        b.classList.toggle('selected', s === limitS[kind])
        b.addEventListener('click', () => {
          limitS[kind] = s
          renderSheet()
        })
        return b
      }),
    )
  }
  durationBox.append(durations)

  const optionsBox = el('div', 'activity-field')
  optionsBox.append(el('span', '', t('activity.options.label')))
  const optionList = el('div', 'activity-options')
  const addOption = button('activity-chip', t('activity.options.add'))
  const optionInput = (): HTMLInputElement => {
    const input = el('input')
    input.type = 'text'
    input.maxLength = ACTIVITY_OPTION_CHARS
    input.placeholder = t('activity.options.placeholder', { n: optionList.children.length + 1 })
    return input
  }
  for (let i = 0; i < ACTIVITY_MIN_OPTIONS; i++) optionList.append(optionInput())
  addOption.addEventListener('click', () => {
    if (optionList.children.length < ACTIVITY_MAX_OPTIONS) optionList.append(optionInput())
    renderSheet()
  })
  // A poll may take several answers from each person.
  const multiLabel = el('label', 'activity-check')
  const multiBox = el('input')
  multiBox.type = 'checkbox'
  multiLabel.append(multiBox, el('span', '', t('activity.poll.multi')))
  optionsBox.append(optionList, addOption, multiLabel)

  const sheetError = el('p', 'activity-error')
  sheetError.setAttribute('role', 'alert')
  const startButton = button('activity-primary', t('activity.start'))
  const sheetHeader = el('div', 'activity-sheet-header')
  sheetHeader.append(sheetTitle, sheetClose)
  sheet.append(sheetHeader, kinds, promptLabel, durationBox, optionsBox, sheetError, startButton)
  // The presenter moves it off what they want to show (owner, 2026-10-12).
  const offSheetDrag = makeDraggable(sheet, sheetHeader)
  // The bars grow and shrink (a pin form, results): the row follows.
  const barSize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => placeRow())

  function renderSheet(): void {
    for (const [k, b] of kindButtons) {
      b.setAttribute('aria-checked', String(k === kind))
      b.classList.toggle('selected', k === kind)
    }
    prompt.placeholder = t(`activity.prompt.placeholder.${kind}`)
    show(optionsBox, hasOptions(kind))
    show(multiLabel, kind === 'poll')
    renderDurations()
    show(addOption, optionList.children.length < ACTIVITY_MAX_OPTIONS)
  }
  function openSheet(open: boolean): void {
    show(sheet, open)
    startPill.setAttribute('aria-expanded', String(open))
    sheetError.textContent = ''
    if (open) {
      renderSheet()
      prompt.focus()
    }
  }
  startPill.addEventListener('click', () => openSheet(sheet.classList.contains('hidden')))
  sheetClose.addEventListener('click', () => openSheet(false))
  sheet.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') {
      ev.stopPropagation()
      openSheet(false)
      startPill.focus()
    }
  })
  startButton.addEventListener('click', () => {
    const text = prompt.value.trim()
    if (!text) {
      sheetError.textContent = t('activity.error.prompt')
      return
    }
    let options: string[] | null = null
    if (hasOptions(kind)) {
      options = [...optionList.querySelectorAll('input')].map((i) => i.value.trim()).filter(Boolean)
      if (options.length < ACTIVITY_MIN_OPTIONS) {
        sheetError.textContent = t('activity.error.options')
        return
      }
    }
    room.send({
      t: 'activity',
      a: { kind, prompt: text, durationMs: limitS[kind] === null ? null : limitS[kind]! * 1000, options, ...(kind === 'poll' && multiBox.checked ? { multi: true } : {}) },
    })
    openSheet(false)
  })

  // --------------------------------------------------------------- the bar
  const bar = el('section', 'activity-bar hidden')
  bar.setAttribute('aria-label', t('activity.bar.aria'))
  // The clock everyone sees: time left, or time so far when there is no limit.
  const timer = el('div', 'activity-timer hidden')
  timer.setAttribute('role', 'timer')
  const promptEl = el('p', 'activity-prompt')
  /** Audience, while exploring: the sphere and Tools are theirs for now. */
  const freeHint = el('p', 'activity-free hidden', t('activity.explore.free'))
  const meta = el('p', 'activity-meta')
  meta.setAttribute('aria-live', 'polite')
  const choices = el('div', 'activity-choices')
  /** The lead's and moderator's list of pins: a pin on the far side of the globe is still one tap away. */
  const pinList = el('ul', 'activity-pin-list')
  const pinForm = el('div', 'activity-pin-form hidden')
  const pinNote = el('input')
  pinNote.type = 'text'
  pinNote.maxLength = PIN_NOTE_CHARS
  pinNote.placeholder = t('activity.explore.note.placeholder')
  const pinSend = button('activity-primary', t('activity.explore.pin.send'))
  const pinCancel = button('', t('activity.explore.pin.cancel'))
  pinForm.append(pinNote, pinSend, pinCancel)
  const actions = el('div', 'activity-actions')
  const endButton = button('', '')
  const revealButton = button('activity-primary', t('activity.reveal'))
  const noAnswerButton = button('', t('activity.reveal.noAnswer'))
  const pinButton = button('activity-primary', t('activity.explore.pin'))
  const sendAnswer = button('activity-primary', t('activity.point.send'))
  const voteButton = button('activity-primary', t('activity.poll.vote'))
  actions.append(revealButton, noAnswerButton, pinButton, sendAnswer, voteButton, endButton)
  const note = el('p', 'activity-note')
  note.setAttribute('role', 'status')
  bar.append(timer, promptEl, freeHint, meta, choices, pinList, pinForm, actions, note)
  root.append(row, sheet, handsList, khipuMenu, bar, spotlightBar, driversBar)
  barSize?.observe(bar)
  barSize?.observe(spotlightBar)
  const people = mountMeetingPeople(root, row, room, { code: opts.code, drag: makeDraggable, globes: opts.globes, gameNameOf: opts.gameNameOf, onLeave: opts.onLeave })

  // ------------------------------------------------------------- the state
  let status: RoomStatus | null = null
  let activityId: string | null = null
  /** Audience: pin mode, the pin about to be dropped, pins dropped. */
  let pinMode = false
  let pendingPin: [number, number] | null = null
  let pinned = 0
  /** Audience: their answer, and whether the room has it. */
  let myPoint: [number, number] | null = null
  let sentPoint: [number, number] | null = null
  let myChoice: number | null = null
  /** Audience: a poll's picks (several, when it takes several), and the picks the room has. */
  let myChoices: number[] = []
  let sentChoices: number[] = []
  /** Lead: tapping the right place before revealing. */
  let revealing = false
  /** Lead and moderator: what the room says. */
  let pins: RoomPin[] = []
  /** The pin list last drawn on the globe. */
  let pinsDrawn: RoomPin[] | null = null
  let answered = 0
  let liveCounts: number[] | null = null
  let lookTimer: ReturnType<typeof setInterval> | null = null
  let lastConnected = false
  /** Presenter and moderator: the raised hands. */
  let hands: RoomHand[] = []
  /** Audience: this person's hand is up. */
  let handUp = false
  /** Presenter: what the room was shown, for "Save as Khipu". */
  const recorder = createMeetingRecorder()
  /** One Khipu per meeting on this page: saving again replaces it on the account. */
  const khipuId = newKhipuId()
  const meetingKhipu = (): Khipu => {
    const day = new Date()
    const k = buildMeetingKhipu(recorder.events(), {
      id: khipuId,
      now: day.getTime(),
      title: { [getLocale()]: t('activity.khipu.name', { date: formatDate(day, { dateStyle: 'medium' }) }) },
      lang: getLocale(),
      titleOf: opts.titleOf,
    })
    return { ...k, slug: `meeting-${day.toISOString().slice(0, 10)}` }
  }
  let recordTimer: ReturnType<typeof setInterval> | null = null

  /** Who runs activities and hands the globe over: the presenter's seat, also while a participant has the globe. */
  const isLead = (): boolean => !!status?.meeting && status.seat === 'presenter'
  const watches = (): boolean => isLead() || status?.seat === 'moderator'
  const audience = (): boolean => !!status?.meeting && !watches()

  function resetForActivity(id: string | null): void {
    activityId = id
    pinMode = false
    pendingPin = null
    pinned = 0
    myPoint = null
    sentPoint = null
    myChoice = null
    myChoices = []
    sentChoices = []
    revealing = false
    pins = []
    pinsDrawn = null
    answered = 0
    liveCounts = null
    pinNote.value = ''
    note.textContent = ''
    globe.clear()
    choices.replaceChildren()
  }

  function stopLooks(): void {
    if (lookTimer !== null) clearInterval(lookTimer)
    lookTimer = null
  }

  function syncLooks(exploring: boolean): void {
    if (!exploring) return stopLooks()
    if (lookTimer !== null) return
    let last = ''
    const report = (): void => {
      const c = globe.center()
      if (!c) return
      const key = `${c[0].toFixed(1)},${c[1].toFixed(1)}`
      if (key === last) return
      last = key
      room.send({ t: 'look', p: c })
    }
    report()
    lookTimer = setInterval(report, LOOK_EVERY_MS)
  }

  function renderChoices(): void {
    const a = status?.activity
    if (!a || !hasOptions(a.kind) || !a.options) {
      choices.replaceChildren()
      return
    }
    const poll = a.kind === 'poll'
    const results = (status?.results?.kind === 'choice' || status?.results?.kind === 'poll') && status.results.id === a.id ? status.results : null
    // A poll's counts are everyone's as they come; a choice's, the presenter's until the reveal.
    const counts = results?.counts ?? (watches() || poll ? liveCounts : null)
    const total = counts ? Math.max(1, counts.reduce((s, n) => s + n, 0)) : 1
    choices.replaceChildren(
      ...a.options.map((option, i) => {
        const b = button('activity-choice', '')
        b.append(el('span', 'activity-choice-label', option))
        if (counts) {
          const n = counts[i] ?? 0
          const fill = el('span', 'activity-choice-bar')
          fill.style.inlineSize = `${Math.round((n / total) * 100)}%`
          b.append(fill, el('span', 'activity-choice-count', String(n)))
        }
        const picked = poll ? myChoices.includes(i) : myChoice === i
        b.classList.toggle('selected', picked)
        b.setAttribute('aria-pressed', String(picked))
        b.disabled = !audience() || !answering(a)
        b.addEventListener('click', () => {
          if (poll && a.multi) {
            // Several: picked here, sent with Vote.
            myChoices = myChoices.includes(i) ? myChoices.filter((c) => c !== i) : [...myChoices, i].sort((x, y) => x - y)
            note.textContent = ''
            render()
            return
          }
          if (poll) {
            myChoices = [i]
            sentChoices = [i]
            room.send({ t: 'answer', id: a.id, p: null, choice: i })
            note.textContent = t('activity.poll.voted')
          } else {
            myChoice = i
            room.send({ t: 'answer', id: a.id, p: null, choice: i })
            note.textContent = t('activity.choice.sent')
          }
          renderChoices()
        })
        return b
      }),
    )
  }

  function renderTimer(): void {
    const a = status?.activity
    const view = a ? timerView(a, Date.now()) : null
    show(timer, !!view)
    if (!view) return
    timer.textContent = view.text
    timer.dataset.state = view.state
    timer.setAttribute('aria-label', view.label)
  }

  function renderMeta(): void {
    const a = status?.activity
    if (!a) {
      meta.textContent = ''
      return
    }
    const parts: string[] = []
    if (watches() && a.kind === 'explore') parts.push(t('activity.explore.pins', { n: pins.length }))
    if (a.kind === 'poll') parts.push(t('activity.poll.votes', { n: status?.results?.kind === 'poll' && status.results.id === a.id ? status.results.voters : answered }))
    else if (watches() && a.kind !== 'explore' && !a.closed) parts.push(t('activity.answered', { n: answered }))
    if (audience() && a.kind === 'explore') parts.push(t('activity.explore.pinned', { n: pinned, max: PINS_PER_PERSON }))
    meta.textContent = parts.join(' · ')
  }

  function renderSpotlight(inMeeting: boolean, a: unknown): void {
    const spot = inMeeting ? status?.spotlight ?? null : null
    show(spotlightBar, !!spot && !a)
    // The presenter and the moderators take it back; the presenter also takes over for a moment (owner, 2026-10-12).
    show(takeBack, !!spot && watches())
    show(takeOver, !!spot && isLead() && !spot.override)
    show(handBack, !!spot && isLead() && spot.override)
    show(giveBack, !!spot && spot.me)
    if (spot) {
      spotlightText.textContent = spot.me
        ? t(spot.override ? 'activity.spotlight.paused' : 'activity.spotlight.me')
        : spot.override && isLead()
          ? spot.name ? t('activity.spotlight.overridden', { name: spot.name }) : t('activity.spotlight.overriddenSomeone')
          : spot.name ? t('activity.spotlight.other', { name: spot.name }) : t('activity.spotlight.someone')
    }
    // Hands: the presenter's and the moderators' list, the audience's own hand.
    const idle = inMeeting && !a && !spot
    show(handsPill, idle && watches() && hands.length > 0)
    handsPill.textContent = t('activity.hands.pill', { n: hands.length })
    if (!(idle && watches() && hands.length > 0)) show(handsList, false)
    show(khipuPill, idle && isLead() && recorder.hasContent() && !!room.snapshot)
    if (!(idle && isLead())) show(khipuMenu, false)
    show(raisePill, idle && audience())
    raisePill.textContent = t(handUp ? 'activity.hand.lower' : 'activity.hand.raise')
    raisePill.setAttribute('aria-pressed', String(handUp))
  }

  function renderHands(): void {
    const globes = opts.globes?.() ?? { count: 1, primary: 0 }
    handsList.replaceChildren(
      el('h2', '', t('activity.hands.title')),
      ...hands.map((hand, i) => {
        const item = el('div', 'activity-pin-item')
        item.append(el('span', 'activity-pin-text', hand.name ?? opts.gameNameOf?.(hand.id) ?? t('activity.hands.someone', { n: i + 1 })))
        const give = button('activity-primary', t('activity.hands.give'))
        give.addEventListener('click', () => {
          room.send({ t: 'spotlight', id: hand.id })
          show(handsList, false)
        })
        item.append(give)
        // AYNI (multi-globe plan): with several globes, one of the others can be given instead.
        for (let slot = 0; slot < globes.count; slot++) {
          if (slot === globes.primary) continue
          const giveGlobe = button('activity-give-globe', t('activity.hands.giveGlobe', { n: slot + 1 }))
          giveGlobe.addEventListener('click', () => {
            room.send({ t: 'drive', slot, id: hand.id })
            show(handsList, false)
          })
          item.append(giveGlobe)
        }
        return item
      }),
    )
  }

  /** AYNI (multi-globe plan): who drives which globe, with Take back (the presenter) or Give it back (a driver). */
  function renderDrivers(inMeeting: boolean, a: unknown): void {
    const drivers = inMeeting ? status?.drivers ?? [] : []
    const me = status?.driving ?? null
    show(driversBar, drivers.length > 0 && !a)
    if (!drivers.length) {
      driversList.replaceChildren()
      return
    }
    driversList.replaceChildren(
      ...(isLead() ? [el('h2', '', t('activity.drivers.title'))] : []),
      ...drivers.flatMap((d) => {
        if (!isLead() && d.slot !== me) return []
        const item = el('div', 'activity-pin-item')
        item.append(el('span', 'activity-pin-text', d.slot === me
          ? t('activity.drivers.me', { n: d.slot + 1 })
          : t('activity.drivers.item', { name: d.name ?? t('activity.drivers.someone'), n: d.slot + 1 })))
        const back = button('activity-primary', t(d.slot === me ? 'activity.drivers.giveBack' : 'activity.drivers.takeBack'))
        back.addEventListener('click', () => room.send({ t: 'drive', slot: d.slot, id: null }))
        item.append(back)
        return [item]
      }),
    )
  }

  function render(): void {
    renderBars()
    placeRow()
  }

  /**
   * AYNI (2026-10-12): the row's pills (People, My answers, Leave…) stay
   * while a bar is up at the top centre (an activity's, or who has the
   * globe), so the row stands just under it, and the windows it opens under
   * the row (unless the presenter moved them).
   */
  function placeRow(): void {
    let bottom = 0
    for (const b of [bar, spotlightBar]) {
      if (b.classList.contains('hidden')) continue
      bottom = Math.max(bottom, b.getBoundingClientRect().bottom)
    }
    // On a narrow screen the row would also sit over what stands in the top start corner — the
    // meeting line, a driver's microphone, the presenter's Go live (live check, 2026-10-13: it
    // covered the phone driver's "Microphone on"): under them then.
    if (bottom > 0) {
      const own = row.getBoundingClientRect()
      for (const corner of document.querySelectorAll<HTMLElement>('.room-chip, .meeting-driver-mic, .meeting-live')) {
        // They come and go with the room (another module's): the row moves when they do.
        barSize?.observe(corner)
        if (corner.classList.contains('hidden')) continue
        const r = corner.getBoundingClientRect()
        if (r.width > 0 && r.height > 0 && r.right > own.left && r.left < own.right && r.bottom > bottom) bottom = r.bottom
      }
    }
    row.style.top = bottom > 0 ? `${Math.round(bottom + 8)}px` : ''
    const below = bottom > 0 ? row.getBoundingClientRect().bottom + 8 : 0
    for (const panel of root.querySelectorAll<HTMLElement>('.activity-sheet:not(.moved)')) panel.style.top = below > 0 ? `${Math.round(below)}px` : ''
  }

  function renderBars(): void {
    const a = status?.activity ?? null
    // Removed, or the meeting ended: the page says so itself (main.ts) and lets go of the room.
    const inMeeting = !!status?.meeting && !!status.connected && !status.kicked && !status.ended
    renderSpotlight(inMeeting, a)
    renderDrivers(inMeeting, a)
    // AYNI (meeting games plan): no activity while a game is on (the room refuses one too).
    show(startPill, inMeeting && isLead() && !a && !status?.spotlight && !status?.game)
    if (!(inMeeting && isLead()) || a) openSheetIfClosed(false)
    show(bar, inMeeting && !!a)
    if (!a || !inMeeting) {
      stopLooks()
      return
    }
    const results = status?.results && status.results.id === a.id ? status.results : null
    const exploring = a.kind === 'explore' && (a.endsAt === null || Date.now() < a.endsAt)
    promptEl.textContent = a.kind === 'explore' && isLead() ? t('activity.explore.lead', { prompt: a.prompt }) : a.prompt

    // Lead: end, reveal; moderator: nothing to press.
    show(endButton, isLead())
    endButton.textContent = t(a.kind === 'explore' ? 'activity.explore.end' : 'activity.end')
    show(revealButton, isLead() && a.kind !== 'explore' && !a.closed && !revealing)
    revealButton.textContent = t(a.kind === 'poll' ? 'activity.poll.close' : 'activity.reveal')
    show(noAnswerButton, isLead() && revealing)
    // Audience.
    show(freeHint, audience() && exploring)
    show(pinButton, audience() && exploring && !pinMode && pinned < PINS_PER_PERSON)
    show(pinForm, audience() && exploring && pinMode && pendingPin !== null)
    show(sendAnswer, audience() && a.kind === 'point' && answering(a) && myPoint !== null && myPoint !== sentPoint)
    show(voteButton, audience() && a.kind === 'poll' && !!a.multi && answering(a) && myChoices.length > 0 && myChoices.join() !== sentChoices.join())

    if (audience() && a.kind === 'explore' && pinMode && !pendingPin) note.textContent = t('activity.explore.pinTap')
    if (audience() && a.kind === 'point' && answering(a) && myPoint === null) note.textContent = t('activity.point.tap')
    if (audience() && a.kind !== 'explore' && !a.closed && timeUp(a)) note.textContent = t('activity.time.answersClosed')
    if (audience() && a.kind === 'poll' && a.multi && answering(a) && myChoices.length === 0 && !sentChoices.length) note.textContent = t('activity.poll.pickMany')
    if (a.kind === 'poll' && (a.closed || timeUp(a)) && !watches()) note.textContent = t('activity.poll.closed')
    if (isLead() && revealing) note.textContent = t('activity.reveal.tap')

    if (results?.kind === 'point') {
      globe.setAnswers(results.points, results.answer)
      if (audience()) {
        note.textContent = results.answer && sentPoint
          ? t('activity.point.distance', { km: Math.round(distanceKm(sentPoint, results.answer)).toLocaleString() })
          : sentPoint ? t('activity.point.noAnswer') : t('activity.point.missed')
      } else {
        note.textContent = t('activity.point.results', { n: results.points.length })
      }
    } else if (audience() && a.closed && a.kind === 'choice') {
      note.textContent = myChoice === null ? t('activity.point.missed') : ''
    }
    // Redrawn only when they change: a redraw closes a pin's open popup.
    if (watches() && a.kind === 'explore' && pinsDrawn !== pins) {
      pinsDrawn = pins
      globe.setPins(pins, isLead() ? showPin : undefined)
      renderPinList()
    }
    show(pinList, watches() && a.kind === 'explore' && pins.length > 0)
    syncLooks(audience() && exploring)
    renderChoices()
    renderMeta()
    renderTimer()
  }

  /** Newest first: who, what, and the ways to see it. */
  function renderPinList(): void {
    pinList.replaceChildren(
      ...[...pins].reverse().map((pin) => {
        const item = el('li', 'activity-pin-item')
        const text = el('span', 'activity-pin-text')
        text.append(el('strong', '', pin.name ?? t('activity.pin.anonymous')), document.createTextNode(pin.note ? ` ${pin.note}` : ''))
        const look = button('', t('activity.pin.look'))
        look.addEventListener('click', () => globe.flyTo([pin.lat, pin.lon]))
        item.append(text, look)
        if (isLead()) {
          const everyone = button('activity-primary', t('activity.pin.show'))
          everyone.addEventListener('click', () => showPin(pin))
          item.append(everyone)
        }
        return item
      }),
    )
  }

  function openSheetIfClosed(open: boolean): void {
    if (!open && !sheet.classList.contains('hidden')) openSheet(false)
  }

  function showPin(pin: RoomPin): void {
    // Everyone back first, then the flight: the room follows the lead's camera on its way there.
    room.send({ t: 'activity', a: null })
    globe.flyTo([pin.lat, pin.lon])
  }

  // ----------------------------------------------------------- the inputs
  handsPill.addEventListener('click', () => {
    const open = handsList.classList.contains('hidden')
    if (open) renderHands()
    show(handsList, open)
    handsPill.setAttribute('aria-expanded', String(open))
  })
  raisePill.addEventListener('click', () => {
    handUp = !handUp
    room.send({ t: 'hand', up: handUp })
    note.textContent = ''
    render()
  })
  khipuPill.addEventListener('click', () => {
    const open = khipuMenu.classList.contains('hidden')
    khipuInfo.textContent = t('activity.khipu.info', { n: recorder.events().length })
    khipuStatus.textContent = ''
    show(khipuSave, opts.canKeep?.() ?? false)
    show(khipuPlay, !!opts.playKhipu)
    show(khipuMenu, open)
    khipuPill.setAttribute('aria-expanded', String(open))
  })
  khipuDownload.addEventListener('click', async () => {
    const { blob, filename } = await zipKhipu(meetingKhipu(), [], { exchange: opts.exchangeOnly?.() ?? false })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = filename
    document.body.append(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(link.href), 10_000)
    khipuStatus.textContent = t('activity.khipu.downloaded', { filename })
  })
  khipuSave.addEventListener('click', async () => {
    khipuStatus.textContent = t('lessons.player.saving')
    const result = await saveKhipu(meetingKhipu())
    khipuStatus.textContent = result.ok
      ? t('lessons.player.saved', { date: formatDate(new Date(result.value.keptUntil), { dateStyle: 'medium' }) })
      : t(result.error === 'too_many' ? 'lessons.player.saveTooMany' : result.error === 'full' ? 'lessons.player.saveFull' : result.error === 'not_configured' ? 'lessons.player.saveUnavailable' : 'lessons.player.saveFailed')
  })
  khipuPlay.addEventListener('click', () => {
    show(khipuMenu, false)
    opts.playKhipu?.(meetingKhipu())
  })
  takeBack.addEventListener('click', () => room.send({ t: 'spotlight', id: null }))
  takeOver.addEventListener('click', () => room.send({ t: 'override', on: true }))
  handBack.addEventListener('click', () => room.send({ t: 'override', on: false }))
  voteButton.addEventListener('click', () => {
    const a = status?.activity
    if (!a || !myChoices.length) return
    sentChoices = [...myChoices]
    room.send({ t: 'answer', id: a.id, p: null, choice: null, choices: sentChoices })
    note.textContent = t('activity.poll.voted')
    render()
  })
  giveBack.addEventListener('click', () => room.send({ t: 'spotlight', id: null }))
  endButton.addEventListener('click', () => room.send({ t: 'activity', a: null }))
  revealButton.addEventListener('click', () => {
    const a = status?.activity
    if (!a) return
    if (a.kind === 'choice' || a.kind === 'poll') {
      room.send({ t: 'reveal', id: a.id, answer: null })
      return
    }
    revealing = true
    render()
  })
  noAnswerButton.addEventListener('click', () => {
    const a = status?.activity
    if (!a) return
    revealing = false
    room.send({ t: 'reveal', id: a.id, answer: null })
  })
  pinButton.addEventListener('click', () => {
    pinMode = true
    pendingPin = null
    render()
  })
  pinCancel.addEventListener('click', () => {
    pinMode = false
    pendingPin = null
    globe.setMine(null)
    note.textContent = ''
    render()
  })
  pinSend.addEventListener('click', () => {
    if (!pendingPin) return
    room.send({ t: 'pin', p: pendingPin, note: pinNote.value.trim() })
    pinned++
    pinMode = false
    pendingPin = null
    pinNote.value = ''
    globe.setMine(null)
    note.textContent = t('activity.explore.sent')
    render()
  })
  sendAnswer.addEventListener('click', () => {
    const a = status?.activity
    if (!a || !myPoint) return
    room.send({ t: 'answer', id: a.id, p: myPoint, choice: null })
    sentPoint = myPoint
    note.textContent = t('activity.point.sent')
    render()
  })

  const offTap = globe.onTap((p) => {
    const a = status?.activity
    if (!a || !status?.meeting) return
    if (isLead() && revealing) {
      revealing = false
      room.send({ t: 'reveal', id: a.id, answer: p })
      return
    }
    if (!audience()) return
    if (a.kind === 'explore' && pinMode) {
      pendingPin = p
      globe.setMine(p)
      note.textContent = ''
      render()
      pinNote.focus()
    } else if (a.kind === 'point' && answering(a)) {
      myPoint = p
      globe.setMine(p)
      note.textContent = ''
      render()
    }
  })

  // --------------------------------------------------------- the room
  const offStatus = room.onStatus((next) => {
    const id = next.activity?.id ?? null
    // A dropped connection says nothing about the activity: a pin being
    // written survives the reconnection unless the room says it changed
    // (owner, 2026-10-12: students' pin forms kept resetting).
    if (next.connected && id !== activityId) resetForActivity(id)
    // A name given earlier in this tab goes with every (re)connection; a
    // raised hand does not (the room forgot it with the old connection).
    if (next.connected && !lastConnected && next.meeting) {
      const name = storedName()
      if (name) room.send({ t: 'name', name })
      handUp = false
    }
    // Given the globe: the hand has done its job.
    if (next.spotlight?.me) handUp = false
    lastConnected = next.connected
    status = next
    people.update(next)
    // The presenter's page notes what the room is shown, from the moment it presents.
    const presenting = next.meeting && next.seat === 'presenter' && !!room.snapshot
    if (presenting) {
      recorder.activity(next.activity, next.results)
      if (recordTimer === null) {
        const sample = (): void => {
          const state = room.snapshot?.()
          if (state) recorder.observe({ datasetId: state.datasetId, view: state.view })
        }
        sample()
        recordTimer = setInterval(sample, RECORD_EVERY_MS)
      }
    }
    render()
  })
  const offData = room.onActivityData((data) => {
    if (data.t === 'people' || data.t === 'log' || data.t === 'logRows' || data.t === 'mine') {
      people.data(data)
      // Back after a reconnection: this person's answer to what is open is theirs again (AYNI, owner 2026-10-12).
      if (data.t === 'mine') restoreMine(data.mine)
      if (data.t !== 'mine') return
    } else if (data.t === 'pins') {
      pins = data.pins
      recorder.pins(data.pins)
    }
    else if (data.t === 'hands') {
      hands = data.hands
      if (!handsList.classList.contains('hidden')) renderHands()
    } else if (data.t === 'looks') globe.setHeat(data.cells)
    else if (data.t === 'tally' && data.tally.id === activityId) {
      answered = data.tally.answered
      liveCounts = data.tally.counts
    }
    render()
  })
  /** What the room has of this person's answer to the open activity, put back on the bar (and the globe). */
  function restoreMine(mine: MyAnswer[]): void {
    const a = status?.activity
    const given = a ? mine.find((m) => m.activity.id === a.id)?.given : undefined
    if (!a || !given) return
    if (given.pins) pinned = Math.max(pinned, given.pins.length)
    if (given.choice !== undefined && a.kind === 'choice') myChoice = given.choice
    if (given.choices && a.kind === 'poll') {
      myChoices = [...given.choices]
      sentChoices = [...given.choices]
    }
    if (given.p && a.kind === 'point' && sentPoint === null) {
      myPoint = given.p
      sentPoint = given.p
      globe.setMine(given.p)
    }
  }

  // The clock ticks while an activity runs; the moment its time is up the
  // whole bar redraws once (the pin button, the answer buttons).
  let timeUpDrawn: string | null = null
  const tick = setInterval(() => {
    const a = status?.activity
    if (!a) return
    renderTimer()
    if (timeUp(a) && timeUpDrawn !== a.id) {
      timeUpDrawn = a.id
      render()
    }
  }, 500)

  return () => {
    clearInterval(tick)
    offSheetDrag()
    barSize?.disconnect()
    if (recordTimer !== null) clearInterval(recordTimer)
    stopLooks()
    offTap()
    offStatus()
    offData()
    globe.clear()
    row.remove()
    sheet.remove()
    handsList.remove()
    khipuMenu.remove()
    bar.remove()
    spotlightBar.remove()
    people.dispose()
  }
}
